import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Review, ModerationStatus } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { formatDate, getStatusColor, getModerationStatusColor } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { Check, X, AlertTriangle, ChevronLeft, ChevronRight, Undo2, BadgeCheck, Trash2 } from 'lucide-react';

interface ReviewDetailDialogProps {
  review: Review | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApprove: (reviewId: string, notes?: string) => void;
  onReject: (reviewId: string, notes?: string) => void;
  onUnapprove: (reviewId: string, notes?: string) => void;
  onDelete?: (reviewId: string) => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

export function ReviewDetailDialog({
  review,
  open,
  onOpenChange,
  onApprove,
  onReject,
  onUnapprove,
  onDelete,
  onNavigate,
  hasPrev = false,
  hasNext = false,
  canEdit = true,
  canDelete = true,
}: ReviewDetailDialogProps) {
  const { t } = useTranslation('reviews');
  const [notes, setNotes] = useState('');
  const [confirmAction, setConfirmAction] = useState<'reject' | 'delete' | null>(null);

  useEffect(() => {
    setNotes('');
    setConfirmAction(null);
  }, [review?.reviewId]);

  useEffect(() => {
    if (!open || !onNavigate) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement) return;
      if (e.key === 'ArrowLeft' && hasPrev) onNavigate('prev');
      if (e.key === 'ArrowRight' && hasNext) onNavigate('next');
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onNavigate, hasPrev, hasNext]);

  if (!review) return null;

  const isRevoked = review.status === 'Pending' && review.autoApproved === true;

  const handleApprove = () => {
    onApprove(review.reviewId, notes || undefined);
    setNotes('');
    onOpenChange(false);
  };

  const handleConfirmAction = () => {
    if (confirmAction === 'reject') {
      onReject(review.reviewId, notes || undefined);
      setNotes('');
      onOpenChange(false);
    } else if (confirmAction === 'delete' && onDelete) {
      onDelete(review.reviewId);
      setNotes('');
      onOpenChange(false);
    }
    setConfirmAction(null);
  };

  const handleUnapprove = () => {
    onUnapprove(review.reviewId, notes || undefined);
    setNotes('');
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          closeLabel={t('detail.closeDetail')}
        >
          {/* Navigation at the top, left-aligned */}
          {onNavigate && (
            <div className="flex items-center gap-4 -mt-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={!hasPrev}
                onClick={() => onNavigate('prev')}
                className="gap-1.5 px-2 h-8 text-sm"
              >
                <ChevronLeft className="h-4 w-4" />
                {t('detail.previousReview')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!hasNext}
                onClick={() => onNavigate('next')}
                className="gap-1.5 px-2 h-8 text-sm"
              >
                {t('detail.nextReview')}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Separator under navigation */}
          <div className="border-b" />

          <DialogHeader>
            <DialogTitle>{t('detail.title')}</DialogTitle>
            <DialogDescription>
              {t('detail.reviewId')} {review.reviewId}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Status Badges - no hover effect */}
            <div className="flex items-center gap-2">
              <Badge className={cn(getStatusColor(review.status), 'hover:opacity-100')}>
                {t(`filters.reviewStatusValues.${review.status}`)}
              </Badge>
              <Badge className={cn(getModerationStatusColor(review.moderationStatus), 'hover:opacity-100')}>
                {t(`filters.moderationStatusValues.${review.moderationStatus}`)}
              </Badge>
              {review.autoApproved && (
                isRevoked ? (
                  <Badge className="bg-red-50 text-red-600 hover:bg-red-50">
                    {t('status.autoApprovalRevoked')}
                  </Badge>
                ) : (
                  <Badge className="bg-blue-50 text-blue-600 hover:bg-blue-50">
                    {t('status.autoApproved')}
                  </Badge>
                )
              )}
              {review.verified && (
                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 gap-1">
                  <BadgeCheck className="h-3 w-3" />
                  {t('detail.verified')}
                </Badge>
              )}
            </div>

            {/* Product and User Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-semibold mb-1">{t('detail.product')}</h4>
                <p className="text-sm">{review.productName}</p>
                {review.productVintage && (
                  <p className="text-xs text-muted-foreground">
                    {t('detail.vintage')} {review.productVintage}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('detail.sku')} {review.productSku}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-1">{t('detail.user')}</h4>
                <p className="text-sm">{review.userName}</p>
                <p className="text-xs text-muted-foreground">
                  {t('detail.customerNumber')} {review.customerNumber}
                </p>
              </div>
            </div>

            {/* Rating with text */}
            <div>
              <h4 className="text-sm font-semibold mb-1">{t('detail.rating')}</h4>
              <div className="flex items-center gap-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    className={i < review.rating ? 'text-yellow-400 text-xl' : 'text-gray-300 text-xl'}
                  >
                    ★
                  </span>
                ))}
                <span className="text-sm text-muted-foreground ml-1">
                  {t('detail.ratingText', { rating: review.rating, max: 5 })}
                </span>
              </div>
            </div>

            {/* Review Content - with fallback for empty values */}
            <div>
              <h4 className="text-sm font-semibold mb-1">{t('detail.reviewTitle')}</h4>
              <p
                className={cn(
                  'text-sm',
                  !review.title ? 'italic text-muted-foreground' : 'font-medium'
                )}
              >
                {review.title || t('detail.noTitle')}
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-1">{t('detail.content')}</h4>
              <p
                className={cn(
                  'text-sm whitespace-pre-wrap',
                  !review.content && 'italic text-muted-foreground'
                )}
              >
                {review.content || t('detail.noContent')}
              </p>
            </div>

            {/* Flags */}
            {review.flags.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  {t('detail.flagsCount', { count: review.flags.length })}
                </h4>
                <div className="space-y-2">
                  {review.flags.map((flag, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded-lg bg-orange-50"
                    >
                      <div>
                        <Badge variant="outline" className="mb-1">
                          {flag.type}
                        </Badge>
                        {flag.details && (
                          <p className="text-xs text-muted-foreground mt-1">{flag.details}</p>
                        )}
                      </div>
                      <div className="text-sm font-medium">
                        {t('detail.confidence', { value: (flag.confidence * 100).toFixed(0) })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Auto-Approval */}
            <div className={cn(
              'p-3 border rounded-lg',
              isRevoked ? 'bg-red-50 border-red-200' : review.autoApproved ? 'bg-blue-50 border-blue-200' : 'bg-muted/50'
            )}>
              <h4 className="text-sm font-semibold mb-2">{t('detail.autoApproval.title')}</h4>
              {isRevoked && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-3 pb-3 border-b border-red-200">
                  <div>
                    <span className="text-muted-foreground">{t('detail.autoApproval.statusLabel')}</span>
                  </div>
                  <div>
                    <span className="text-red-600 font-medium">{t('status.autoApprovalRevoked')}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{t('detail.autoApproval.checkedAt')}</span>
                  </div>
                  <div>
                    <span>{review.moderatedAt ? formatDate(review.moderatedAt) : '–'}</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <div>
                  <span className="text-muted-foreground">{t('detail.autoApproval.statusLabel')}</span>
                </div>
                <div>
                  <span className={review.autoApproved ? 'text-blue-600 font-medium' : ''}>
                    {review.autoApproved
                      ? t('detail.autoApproval.approved')
                      : t('detail.autoApproval.notApproved')}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {review.autoApproved
                      ? t('detail.autoApproval.basisLabel')
                      : t('detail.autoApproval.reasonLabel')}
                  </span>
                </div>
                <div>
                  {review.autoApproved ? (
                    <span>{t('detail.autoApproval.basisRatingClean', { rating: review.rating })}</span>
                  ) : (
                    <span>
                      {[
                        review.moderationStatus === ModerationStatus.PreFlagged
                          ? t('detail.autoApproval.reasonWordFilter')
                          : null,
                        review.rating < 4
                          ? t('detail.autoApproval.reasonLowRating')
                          : null,
                      ].filter(Boolean).join(', ') || t('detail.autoApproval.reasonUnknown')}
                    </span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">{t('detail.autoApproval.checkedAt')}</span>
                </div>
                <div>
                  <span>{formatDate(review.updatedAt)}</span>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold mb-1">{t('detail.created')}</h4>
                <p className="text-muted-foreground">{formatDate(review.createdAt)}</p>
              </div>
              <div>
                <h4 className="font-semibold mb-1">{t('detail.updated')}</h4>
                <p className="text-muted-foreground">{formatDate(review.updatedAt)}</p>
              </div>
              {review.moderatedAt && (
                <>
                  <div>
                    <h4 className="font-semibold mb-1">{t('detail.moderated')}</h4>
                    <p className="text-muted-foreground">{formatDate(review.moderatedAt)}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">{t('detail.moderatedBy')}</h4>
                    <p className="text-muted-foreground">{review.moderatedBy}</p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Separator above buttons */}
          <div className="border-t pt-4">
            {review.status === 'Pending' && (
              <DialogFooter className="sm:justify-between">
                {onDelete ? (
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmAction('delete')}
                    disabled={!canDelete}
                    className={cn('gap-2', !canDelete && 'opacity-40 pointer-events-none')}
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('actions.delete')}
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    {t('actions.cancel')}
                  </Button>
                  <Button
                    onClick={() => setConfirmAction('reject')}
                    variant="destructive"
                    disabled={!canEdit}
                    className={cn('gap-2', !canEdit && 'opacity-40 pointer-events-none')}
                  >
                    <X className="h-4 w-4" />
                    {t('actions.reject')}
                  </Button>
                  <Button
                    onClick={handleApprove}
                    disabled={!canEdit}
                    className={cn('bg-green-600 hover:bg-green-700 gap-2', !canEdit && 'opacity-40 pointer-events-none')}
                  >
                    <Check className="h-4 w-4" />
                    {t('actions.approve')}
                  </Button>
                </div>
              </DialogFooter>
            )}

            {review.status === 'Approved' && (
              <DialogFooter className="sm:justify-between">
                {onDelete ? (
                  <Button
                    variant="destructive"
                    onClick={() => setConfirmAction('delete')}
                    disabled={!canDelete}
                    className={cn('gap-2', !canDelete && 'opacity-40 pointer-events-none')}
                  >
                    <Trash2 className="h-4 w-4" />
                    {t('actions.delete')}
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => onOpenChange(false)}>
                    {t('actions.cancel')}
                  </Button>
                  <Button
                    onClick={handleUnapprove}
                    disabled={!canEdit}
                    className={cn('bg-amber-600 hover:bg-amber-700 gap-2', !canEdit && 'opacity-40 pointer-events-none')}
                  >
                    <Undo2 className="h-4 w-4" />
                    {t('actions.unapprove')}
                  </Button>
                </div>
              </DialogFooter>
            )}

            {review.status === 'Rejected' && onDelete && (
              <DialogFooter className="sm:justify-between">
                <Button
                  variant="destructive"
                  onClick={() => setConfirmAction('delete')}
                  disabled={!canDelete}
                  className={cn('gap-2', !canDelete && 'opacity-40 pointer-events-none')}
                >
                  <Trash2 className="h-4 w-4" />
                  {t('actions.delete')}
                </Button>
                <Button variant="outline" onClick={() => onOpenChange(false)}>
                  {t('actions.cancel')}
                </Button>
              </DialogFooter>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={!!confirmAction}
        onOpenChange={(open) => { if (!open) setConfirmAction(null); }}
        title={
          confirmAction === 'delete'
            ? t('confirm.deleteTitle')
            : t('confirm.rejectTitle')
        }
        description={
          confirmAction === 'delete'
            ? t('confirm.deleteDescription')
            : t('confirm.rejectDescription')
        }
        confirmLabel={confirmAction === 'delete' ? t('confirm.deleteConfirm') : t('confirm.confirm')}
        cancelLabel={t('confirm.cancel')}
        variant="destructive"
        onConfirm={handleConfirmAction}
      />
    </>
  );
}
